import { Controller } from "@hotwired/stimulus";
/**
 * Flow 4 anchored location dropdown (mimics the ahoydays.com search
 * treatment): a search input over a grouped list where country/region
 * headers are non-selectable and only ports commit a selection.
 *
 * Expected wiring (see search.blade.php / homepage.blade.php /
 * forms/site-enquiry.blade.php):
 * - wrapper: data-controller="location-picker"
 *   data-action="keydown->location-picker#onKeydown click@window->location-picker#onWindowClick"
 * - trigger button: target "trigger", data-action="location-picker#toggle"
 * - hidden inputs: target "input" (locationId), optional "name" (title) and
 *   optional "uid" (location uid — the homepage AJAX search keys on uid)
 * - <x-location-picker> renders the panel/search/list/option/group/empty targets.
 *
 * Selection contract matches the old locations-menu-dialog flow: write the
 * hidden inputs, update the trigger label, close. The locationId change event
 * is what search-form-controller listens to for auto-submit, so it is
 * dispatched last.
 *
 * Mobile (<768px) hardening: while open the page scroll is locked (so a
 * stray scroll cannot detach or bury the panel — on the homepage the sticky
 * clone bar would otherwise slide in over it) and the panel is pinned to the
 * top of the *visual* viewport as a sheet. On every viewport change (keyboard
 * open/close, URL bar collapse) the list is re-capped so the search input
 * stays visible and only the list scrolls internally.
 */
export default class default_1 extends Controller {
    constructor() {
        super(...arguments);
        this.activeIndex = -1;
        this.mobileOpen = false;
        this.scrollLockY = 0;
        this.viewportHandler = null;
    }
    disconnect() {
        // Page teardown while open must not leave the body scroll-locked.
        this.close();
    }
    toggle() {
        if (this.panelTarget.hidden) {
            this.open();
        }
        else {
            this.close();
        }
    }
    open() {
        var _a, _b;
        this.panelTarget.hidden = false;
        this.triggerTarget.setAttribute("aria-expanded", "true");
        this.searchTarget.value = "";
        this.applyFilter("");
        this.listTarget.scrollTop = 0;
        this.mobileOpen = window.matchMedia("(max-width: 767px)").matches;
        if (this.mobileOpen) {
            this.lockScroll();
            this.pinPanel();
        }
        this.fitPanelToViewport();
        this.viewportHandler = () => this.fitPanelToViewport();
        (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.addEventListener("resize", this.viewportHandler);
        (_b = window.visualViewport) === null || _b === void 0 ? void 0 : _b.addEventListener("scroll", this.viewportHandler);
        window.addEventListener("resize", this.viewportHandler);
        this.searchTarget.focus();
    }
    close() {
        var _a, _b;
        if (this.panelTarget.hidden) {
            return;
        }
        if (this.viewportHandler) {
            (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.removeEventListener("resize", this.viewportHandler);
            (_b = window.visualViewport) === null || _b === void 0 ? void 0 : _b.removeEventListener("scroll", this.viewportHandler);
            window.removeEventListener("resize", this.viewportHandler);
            this.viewportHandler = null;
        }
        if (this.mobileOpen) {
            this.unpinPanel();
            this.unlockScroll();
            this.mobileOpen = false;
        }
        this.listTarget.style.maxHeight = "";
        this.panelTarget.hidden = true;
        this.triggerTarget.setAttribute("aria-expanded", "false");
        this.setActive(-1);
    }
    filter() {
        this.applyFilter(this.searchTarget.value);
    }
    select(event) {
        // currentTarget, not target: the click may land on the context <span>.
        this.commit(event.currentTarget);
    }
    onKeydown(event) {
        if (this.panelTarget.hidden) {
            return;
        }
        switch (event.key) {
            case "Escape":
                event.preventDefault();
                this.close();
                this.triggerTarget.focus();
                break;
            case "ArrowDown":
                event.preventDefault();
                this.moveActive(1);
                break;
            case "ArrowUp":
                event.preventDefault();
                this.moveActive(-1);
                break;
            case "Enter": {
                // The search input lives inside the page's search <form>; Enter must
                // select, never submit the form with a half-typed query.
                if (event.target !== this.searchTarget) {
                    return;
                }
                event.preventDefault();
                const options = this.visibleOptions();
                const chosen = options[this.activeIndex] || options[0];
                if (chosen) {
                    this.commit(chosen);
                }
                break;
            }
        }
    }
    onWindowClick(event) {
        if (this.panelTarget.hidden) {
            return;
        }
        if (event.target instanceof Node && this.element.contains(event.target)) {
            return;
        }
        this.close();
    }
    commit(option) {
        const title = option.dataset.title || "";
        if (this.hasNameTarget) {
            this.nameTarget.value = title;
            this.nameTarget.dispatchEvent(new Event("change"));
        }
        if (this.hasUidTarget) {
            this.uidTarget.value = option.dataset.uid || "";
            this.uidTarget.dispatchEvent(new Event("change"));
        }
        this.triggerTarget.innerText = title || this.triggerTarget.dataset.placeholder || "Add Location";
        this.close();
        // Last: on the search page this change listener submits the form.
        this.inputTarget.value = option.dataset.locationId || "";
        this.inputTarget.dispatchEvent(new Event("change"));
    }
    /**
     * Cap the panel to the visual viewport so the search input stays on
     * screen and the list scrolls internally — including with the soft
     * keyboard open, which shrinks the visual viewport but not 100vh.
     */
    fitPanelToViewport() {
        if (this.panelTarget.hidden) {
            return;
        }
        const viewport = window.visualViewport;
        const viewportTop = viewport ? viewport.offsetTop : 0;
        const viewportLeft = viewport ? viewport.offsetLeft : 0;
        const viewportHeight = viewport ? viewport.height : window.innerHeight;
        const viewportWidth = viewport ? viewport.width : window.innerWidth;
        const gutter = 12;
        if (this.mobileOpen) {
            // Pin as a sheet at the top of the visual viewport. position:fixed is
            // re-based by any transformed ancestor (the homepage sticky clone bar
            // carries a translate), so measure where the panel actually landed and
            // compensate for the offset.
            const style = this.panelTarget.style;
            const wantTop = viewportTop + gutter;
            const wantLeft = viewportLeft + gutter;
            style.top = `${wantTop}px`;
            style.left = `${wantLeft}px`;
            style.width = `${Math.max(200, viewportWidth - gutter * 2)}px`;
            const rect = this.panelTarget.getBoundingClientRect();
            const deltaTop = rect.top - wantTop;
            const deltaLeft = rect.left - wantLeft;
            if (Math.abs(deltaTop) > 1 || Math.abs(deltaLeft) > 1) {
                style.top = `${wantTop - deltaTop}px`;
                style.left = `${wantLeft - deltaLeft}px`;
            }
        }
        const panelTop = this.panelTarget.getBoundingClientRect().top;
        const chrome = this.panelTarget.offsetHeight - this.listTarget.offsetHeight;
        const available = viewportTop + viewportHeight - panelTop - gutter - chrome;
        this.listTarget.style.maxHeight = `${Math.max(96, Math.round(available))}px`;
    }
    pinPanel() {
        const style = this.panelTarget.style;
        style.position = "fixed";
        style.top = "0px";
        style.left = "0px";
        style.right = "auto";
        style.bottom = "auto";
        style.marginTop = "0";
        style.maxWidth = "none";
        style.zIndex = "100";
    }
    unpinPanel() {
        const style = this.panelTarget.style;
        style.position = "";
        style.top = "";
        style.left = "";
        style.right = "";
        style.bottom = "";
        style.marginTop = "";
        style.width = "";
        style.maxWidth = "";
        style.zIndex = "";
    }
    /**
     * body position:fixed is the scroll lock that iOS Safari actually honours
     * (overflow:hidden historically does not stop touch scrolling there).
     */
    lockScroll() {
        this.scrollLockY = window.scrollY;
        const style = document.body.style;
        style.position = "fixed";
        style.top = `-${this.scrollLockY}px`;
        style.left = "0";
        style.right = "0";
        style.width = "100%";
    }
    unlockScroll() {
        const style = document.body.style;
        style.position = "";
        style.top = "";
        style.left = "";
        style.right = "";
        style.width = "";
        window.scrollTo(0, this.scrollLockY);
    }
    applyFilter(query) {
        const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
        const visibleGroupKeys = new Set();
        let anyVisible = false;
        this.optionTargets.forEach((option) => {
            const haystack = option.dataset.searchText || "";
            const matches = tokens.every((token) => haystack.includes(token));
            const row = option.closest("li");
            if (row) {
                row.hidden = !matches;
            }
            if (matches) {
                anyVisible = true;
                (option.dataset.groupKeys || "")
                    .split(" ")
                    .filter(Boolean)
                    .forEach((key) => visibleGroupKeys.add(key));
            }
        });
        this.groupTargets.forEach((group) => {
            group.hidden = !visibleGroupKeys.has(group.dataset.groupKey || "");
        });
        this.emptyTarget.hidden = anyVisible;
        this.setActive(-1);
    }
    visibleOptions() {
        return this.optionTargets.filter((option) => { var _a; return !((_a = option.closest("li")) === null || _a === void 0 ? void 0 : _a.hidden); });
    }
    moveActive(delta) {
        const options = this.visibleOptions();
        if (options.length === 0) {
            return;
        }
        const next = Math.min(Math.max(this.activeIndex + delta, 0), options.length - 1);
        this.setActive(next);
    }
    setActive(index) {
        this.activeIndex = index;
        this.optionTargets.forEach((option) => {
            delete option.dataset.active;
        });
        const active = this.visibleOptions()[index];
        if (active) {
            active.dataset.active = "";
            active.scrollIntoView({ block: "nearest" });
        }
    }
}
default_1.targets = ["trigger", "panel", "search", "list", "option", "group", "empty", "input", "name", "uid"];
